/**
 * useBulkSetField
 *
 * Sequential bulk save for multi-row field updates.
 * Calls frappe.client.set_value directly (NOT saveCell) to avoid
 * N query invalidations. One final invalidateQueries after all rows.
 */

import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { frappeMethod } from '@/lib/erp/utils';
import type { Row } from '@tanstack/react-table';

export interface BulkSetResult {
  total: number;
  succeeded: number;
  failed: Array<{ name: string; error: string }>;
}

export interface BulkSetRow {
  name: string;
  oldValue: unknown;
}

/**
 * Extract row names and current field values from selected table rows.
 */
export function buildRowsForBulkSet<T>(
  rows: Row<T>[],
  getRowId: (row: T) => string,
  fieldname: string,
): BulkSetRow[] {
  return rows.map((row) => ({
    name: getRowId(row.original),
    oldValue: (row.original as Record<string, unknown>)[fieldname],
  }));
}

export function useBulkSetField(doctype: string) {
  const queryClient = useQueryClient();
  const busyRef = useRef(false);

  /**
   * Optimistically update all matching list query caches for a single row/field.
   * Returns a rollback function.
   */
  const optimisticUpdate = useCallback(
    (name: string, fieldname: string, newValue: unknown): (() => void) => {
      const snapshots: Array<{ queryKey: unknown[]; data: unknown }> = [];

      queryClient.setQueriesData<unknown>(
        { queryKey: ['doctype-list', doctype] },
        (old: unknown) => {
          if (!old) return old;
          snapshots.push({ queryKey: ['doctype-list', doctype], data: structuredClone(old) });

          // Handle FrappeListResponse shape: { data: T[], total, pageLength }
          if (
            typeof old === 'object' &&
            old !== null &&
            'data' in old &&
            Array.isArray((old as { data: unknown[] }).data)
          ) {
            const resp = old as { data: Record<string, unknown>[] };
            return {
              ...resp,
              data: resp.data.map((row) =>
                row.name === name ? { ...row, [fieldname]: newValue } : row,
              ),
            };
          }
          // Handle plain array shape
          if (Array.isArray(old)) {
            return (old as Record<string, unknown>[]).map((row) =>
              row.name === name ? { ...row, [fieldname]: newValue } : row,
            );
          }
          return old;
        },
      );

      return () => {
        for (const snap of snapshots) {
          queryClient.setQueriesData({ queryKey: snap.queryKey }, () => snap.data);
        }
      };
    },
    [queryClient, doctype],
  );

  const bulkSet = useCallback(
    async (
      rows: BulkSetRow[],
      fieldname: string,
      value: unknown,
    ): Promise<BulkSetResult> => {
      if (busyRef.current) {
        return { total: 0, succeeded: 0, failed: [] };
      }
      busyRef.current = true;

      // Filter out rows where value is unchanged
      const rowsToUpdate = rows.filter((r) => r.oldValue !== value);
      const total = rowsToUpdate.length;

      if (total === 0) {
        busyRef.current = false;
        return { total: 0, succeeded: 0, failed: [] };
      }

      const toastId = toast.loading(`Updating ${total} items...`);
      const result: BulkSetResult = { total, succeeded: 0, failed: [] };
      const rollbacks: Array<() => void> = [];

      for (let i = 0; i < rowsToUpdate.length; i++) {
        const row = rowsToUpdate[i];

        // Update progress toast
        if (total > 3) {
          toast.loading(`Updating ${i + 1}/${total}...`, { id: toastId });
        }

        // Optimistic update
        const rollback = optimisticUpdate(row.name, fieldname, value);
        rollbacks.push(rollback);

        try {
          await frappeMethod<Record<string, unknown>>('frappe.client.set_value', {
            doctype,
            name: row.name,
            fieldname: { [fieldname]: value },
          });
          result.succeeded++;
        } catch (err: unknown) {
          // Rollback this row's optimistic update
          rollback();
          const message =
            err instanceof Error ? err.message : 'Failed to save';
          result.failed.push({ name: row.name, error: message });
        }
      }

      // One final cache invalidation for eventual consistency
      queryClient.invalidateQueries({ queryKey: ['doctype-list', doctype] });

      // Show result toast
      if (result.failed.length === 0) {
        toast.success(`${result.succeeded} item${result.succeeded !== 1 ? 's' : ''} updated`, {
          id: toastId,
        });
      } else if (result.succeeded > 0) {
        toast.warning(
          `${result.succeeded} updated, ${result.failed.length} failed`,
          {
            id: toastId,
            description: result.failed.map((f) => `${f.name}: ${f.error}`).join('\n'),
          },
        );
      } else {
        toast.error(`All ${result.failed.length} updates failed`, {
          id: toastId,
          description: result.failed.map((f) => `${f.name}: ${f.error}`).join('\n'),
        });
      }

      busyRef.current = false;
      return result;
    },
    [doctype, queryClient, optimisticUpdate],
  );

  return { bulkSet, isBusy: busyRef };
}
